import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { listStudents, listResourceVariants, generateVariants } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { EXPORT_GROUPINGS, PAGE_FORMATS, getGroupedPositions } from "./pageFormats";
import {
  Download,
  Users,
  User,
  FileText,
  Grid3X3,
  Printer,
  Loader,
  Eye,
  CheckCircle2,
  RefreshCw,
} from "lucide-react";

const NIVEAU_COLORS = {
  gs: "bg-pink-100 text-pink-700",
  cp: "bg-blue-100 text-blue-700",
  ce1: "bg-green-100 text-green-700",
  ce2: "bg-yellow-100 text-yellow-700",
  cm1: "bg-orange-100 text-orange-700",
  cm2: "bg-red-100 text-red-700",
};

/**
 * ExportOutputDialog - Export dialog with student variants and grouping options
 */
export function ExportOutputDialog({
  open,
  onOpenChange,
  resourceId,
  documentTitle,
  onExportPNG,
  onExportPDF,
  onExportGrouped,
  stageRef,
}) {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [exportMode, setExportMode] = useState("single"); // single, grouped, students
  const [grouping, setGrouping] = useState(1);
  const [outputFormat, setOutputFormat] = useState("pdf"); // pdf, png
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (open && user?.role === "teacher") {
      fetchData();
    }
  }, [open, user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [studentsRes, variantsRes] = await Promise.all([
        listStudents(),
        resourceId ? listResourceVariants(resourceId).catch(() => ({ data: { variants: [] } })) : Promise.resolve({ data: { variants: [] } }),
      ]);
      
      setStudents(studentsRes.data || []);
      setVariants(variantsRes.data?.variants || []);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStudent = (studentId) => {
    setSelectedStudentIds(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      }
      return [...prev, studentId];
    });
  };

  const handleSelectAll = () => {
    setSelectedStudentIds(students.map(s => s.id));
  };

  const handleDeselectAll = () => {
    setSelectedStudentIds([]);
  };

  const handleGenerateVariants = async () => {
    if (!resourceId || selectedStudentIds.length === 0) return;
    
    setGenerating(true);
    try {
      await generateVariants(resourceId, selectedStudentIds);
      await fetchData(); // Refresh variants list
    } catch (err) {
      console.error("Error generating variants:", err);
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    
    try {
      if (exportMode === "single") {
        if (outputFormat === "pdf") {
          await onExportPDF?.();
        } else {
          await onExportPNG?.();
        }
      } else if (exportMode === "grouped") {
        await onExportGrouped?.(grouping, outputFormat);
      } else if (exportMode === "students") {
        // Export each selected student's variant
        for (const studentId of selectedStudentIds) {
          const variant = variants.find(v => v.target_student_id === studentId);
          if (variant) {
            // Would export variant here
            console.log("Exporting variant for", studentId);
          }
        }
      }
    } catch (err) {
      console.error("Export error:", err);
    } finally {
      setExporting(false);
      onOpenChange(false);
    }
  };

  const getStudentVariantStatus = (studentId) => {
    return variants.some(v => v.target_student_id === studentId);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-green-500" />
            Exporter la ressource
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-6">
          {/* Export Mode Selection */}
          <div>
            <Label className="text-sm font-medium text-slate-600 mb-2 block">
              Mode d'export
            </Label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setExportMode("single")}
                className={`p-3 rounded-lg border-2 text-center transition-all ${
                  exportMode === "single"
                    ? "border-indigo-500 bg-indigo-50"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <FileText className="w-6 h-6 mx-auto mb-1 text-slate-600" />
                <span className="text-sm font-medium">Simple</span>
              </button>
              
              <button
                onClick={() => setExportMode("grouped")}
                className={`p-3 rounded-lg border-2 text-center transition-all ${
                  exportMode === "grouped"
                    ? "border-indigo-500 bg-indigo-50"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <Grid3X3 className="w-6 h-6 mx-auto mb-1 text-slate-600" />
                <span className="text-sm font-medium">Groupe</span>
              </button>
              
              <button
                onClick={() => setExportMode("students")}
                className={`p-3 rounded-lg border-2 text-center transition-all ${
                  exportMode === "students"
                    ? "border-indigo-500 bg-indigo-50"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <Users className="w-6 h-6 mx-auto mb-1 text-slate-600" />
                <span className="text-sm font-medium">Eleves</span>
              </button>
            </div>
          </div>

          {/* Grouping Options (only for grouped mode) */}
          {exportMode === "grouped" && (
            <div>
              <Label className="text-sm font-medium text-slate-600 mb-2 block">
                Nombre par page
              </Label>
              <div className="grid grid-cols-5 gap-2">
                {EXPORT_GROUPINGS.map((g) => (
                  <button
                    key={g.id}
                    onClick={() => setGrouping(g.id)}
                    className={`py-3 rounded-lg border-2 text-center transition-all ${
                      grouping === g.id
                        ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                        : "border-slate-200 hover:border-slate-300 text-slate-600"
                    }`}
                  >
                    <span className="font-bold text-lg">{g.id}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Student Selection (only for students mode) */}
          {exportMode === "students" && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium text-slate-600">
                  Eleves ({selectedStudentIds.length} selectionne(s))
                </Label>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                    Tous
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleDeselectAll}>
                    Aucun
                  </Button>
                </div>
              </div>
              
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="w-6 h-6 animate-spin text-indigo-500" />
                </div>
              ) : students.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>Aucun eleve</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {students.map((student) => {
                    const hasVariant = getStudentVariantStatus(student.id);
                    const colors = NIVEAU_COLORS[student.niveau] || NIVEAU_COLORS.cp;
                    
                    return (
                      <div
                        key={student.id}
                        onClick={() => handleToggleStudent(student.id)}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedStudentIds.includes(student.id)
                            ? "border-indigo-500 bg-indigo-50"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <Checkbox
                          checked={selectedStudentIds.includes(student.id)}
                          className="pointer-events-none"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">
                            {student.student_name}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className={`text-xs ${colors}`}>
                              {student.niveau?.toUpperCase()}
                            </Badge>
                            {hasVariant && (
                              <Badge className="text-xs bg-green-100 text-green-700">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Variant
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Generate Variants Button */}
              {selectedStudentIds.length > 0 && resourceId && (
                <Button
                  variant="outline"
                  className="w-full mt-3"
                  onClick={handleGenerateVariants}
                  disabled={generating}
                >
                  {generating ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Generation...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Generer les variantes ({selectedStudentIds.length})
                    </>
                  )}
                </Button>
              )}
            </div>
          )}

          {/* Output Format */}
          <div>
            <Label className="text-sm font-medium text-slate-600 mb-2 block">
              Format de sortie
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setOutputFormat("pdf")}
                className={`p-3 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                  outputFormat === "pdf"
                    ? "border-red-500 bg-red-50 text-red-700"
                    : "border-slate-200 hover:border-slate-300 text-slate-600"
                }`}
              >
                <FileText className="w-5 h-5" />
                <span className="font-medium">PDF</span>
              </button>
              
              <button
                onClick={() => setOutputFormat("png")}
                className={`p-3 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                  outputFormat === "png"
                    ? "border-green-500 bg-green-50 text-green-700"
                    : "border-slate-200 hover:border-slate-300 text-slate-600"
                }`}
              >
                <Printer className="w-5 h-5" />
                <span className="font-medium">PNG</span>
              </button>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting || (exportMode === "students" && selectedStudentIds.length === 0)}
            className="bg-green-600 hover:bg-green-700"
          >
            {exporting ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Export...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Exporter
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
